import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight, Check, Eye, Feather, Sunrise } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/lib/api";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }
});

export default function HomePage() {
  const [price, setPrice] = useState(null);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    API.get("/price").then(res => setPrice(res.data)).catch(() => {});
  }, []);

  const handleCTA = () => {
    if (user?.has_paid) navigate('/dashboard');
    else if (user) navigate('/betalning');
    else navigate('/registrera');
  };

  return (
    <div className="min-h-screen bg-white" data-testid="homepage">
      <Navigation />

      {/* Hero — Welcome */}
      <section className="pt-32 sm:pt-40 pb-16 sm:pb-24 px-5 sm:px-6">
        <div className="max-w-2xl mx-auto text-center">
          <motion.p {...fadeUp(0)}
            className="text-sm sm:text-base text-[#0A0A0A] mb-6 leading-relaxed">
            Varmt välkommen till
          </motion.p>

          <motion.h1 {...fadeUp(0.08)}
            className="heading-font text-4xl sm:text-5xl lg:text-6xl font-medium text-[#0A0A0A] tracking-tight leading-[1.1] mb-6">
            Emotionell Frihet<sup className="text-base sm:text-lg align-super ml-0.5">™</sup>
          </motion.h1>

          <motion.p {...fadeUp(0.16)}
            className="text-base sm:text-lg text-[#0A0A0A] max-w-xl mx-auto leading-relaxed mb-3">
            En metod skapad av mig, Leila Dadgar — för dig som vill förstå dig själv på djupet och skapa verklig förändring, inifrån och ut.
          </motion.p>

          <motion.p {...fadeUp(0.22)}
            className="text-sm sm:text-base text-[#333333] max-w-md mx-auto leading-relaxed">
            En guidad inre resa i tre faser och nio steg. I din egen takt.
          </motion.p>

          <motion.div {...fadeUp(0.28)} className="mt-8">
            <Button onClick={handleCTA} data-testid="hero-cta-btn"
              className="bg-[#A0655A] hover:bg-[#8B5549] text-white rounded-full px-10 py-6 text-base sm:text-lg shadow-lg shadow-[#A0655A]/15 hover:shadow-xl transition-all">
              {user?.has_paid ? 'Fortsätt din resa' : user ? 'Gå till betalning' : 'Börja din resa'}
              <ChevronRight className="ml-2" size={20} />
            </Button>
            {price && !user?.has_paid && (
              <p className="mt-3 text-sm text-[#777]">Engångsbetalning — {price.price} kr</p>
            )}
          </motion.div>
        </div>
      </section>

      {/* The three phases */}
      <section className="pb-16 sm:pb-24 px-5 sm:px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div {...fadeUp()} className="text-center mb-10 sm:mb-12">
            <h2 className="heading-font text-2xl sm:text-3xl font-medium text-[#0A0A0A] mb-3">
              Tre faser. Nio steg.
            </h2>
            <p className="text-[#333333] max-w-lg mx-auto leading-relaxed text-sm sm:text-base">
              Varje fas bygger vidare på den förra. Du guidas genom reflektioner, övningar och verktyg — hela vägen.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5">
            {[
              { icon: Eye, num: "Fas 1", title: "Klarhet", subtitle: "Att se", desc: "Du börjar med att se dig själv tydligare — dina mönster, triggers och den inre dialogen som styr dig.", steps: ["Att stanna upp", "Dina reaktioner och mönster", "Din inre röst"] },
              { icon: Feather, num: "Fas 2", title: "Frigörelse", subtitle: "Att släppa", desc: "Du möter det som gör ont och börjar släppa det som inte längre är ditt att bära.", steps: ["Känslor du bär på", "Att lägga ner det som inte är ditt", "Vem är du utan det gamla?"] },
              { icon: Sunrise, num: "Fas 3", title: "Förankring", subtitle: "Att vara", desc: "Du landar i dig själv och lär dig att leva närmare din egen sanning.", steps: ["Att börja välja annorlunda", "Gränser, trygghet och självmedkänsla", "Att leva fritt"] },
            ].map((phase, i) => (
              <motion.div key={i} {...fadeUp(0.06 * i)}
                className="bg-[#FEFCFA] border border-[#E8E0DB] rounded-2xl p-6 sm:p-7 text-center"
                data-testid={`phase-card-${i + 1}`}>
                <div className="w-11 h-11 rounded-full bg-[#F0E8E3] text-[#A0655A] flex items-center justify-center mx-auto mb-4">
                  <phase.icon size={20} strokeWidth={1.8} />
                </div>
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#A0655A] font-medium mb-1">{phase.num}</p>
                <h3 className="heading-font text-xl sm:text-2xl font-medium text-[#0A0A0A] mb-1">{phase.title}</h3>
                <p className="text-xs text-[#A0655A] font-medium uppercase tracking-wider mb-3">{phase.subtitle}</p>
                <p className="text-sm text-[#333333] leading-relaxed mb-4">{phase.desc}</p>
                <div className="space-y-1.5 pt-3 border-t border-[#E8E0DB]/50">
                  {phase.steps.map((s, j) => (
                    <p key={j} className="text-xs text-[#555] leading-snug">{j + 1}. {s}</p>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing & CTA */}
      {!user?.has_paid && (
        <section className="py-16 sm:py-24 px-5 sm:px-6 bg-[#F5F0EB]" data-testid="pricing-section">
          <div className="max-w-2xl mx-auto text-center">
            <motion.div {...fadeUp()}>
              <h2 className="heading-font text-2xl sm:text-3xl font-medium text-[#0A0A0A] mb-3">
                Få tillgång till hela metoden
              </h2>
              <p className="text-[#333333] mb-10 max-w-md mx-auto leading-relaxed text-sm sm:text-base">
                En engångsbetalning. Inga prenumerationer eller dolda kostnader.
              </p>
            </motion.div>

            <motion.div {...fadeUp(0.1)}
              className="bg-white rounded-2xl p-8 sm:p-10 border border-[#E8E0DB] max-w-sm mx-auto mb-8">
              <div className="text-4xl sm:text-5xl font-medium text-[#0A0A0A] mb-1">
                {price?.price || 199} <span className="text-lg sm:text-xl">kr</span>
              </div>
              <p className="text-[#555555] text-sm mb-8">Engångsbetalning</p>

              <ul className="text-left space-y-3 mb-8">
                {[
                  "Alla 9 steg med texter och reflektioner",
                  "Guidade övningar kopplade till varje fas",
                  "Psykologiska självtester",
                  "Personlig dagbok",
                  "Incheckning och uppföljning",
                ].map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-[#1A1A1A] text-sm">
                    <Check className="text-[#A0655A] mt-0.5 flex-shrink-0" size={15} strokeWidth={2.5} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>

              <Button onClick={handleCTA}
                className="w-full bg-[#A0655A] hover:bg-[#8B5549] text-white rounded-full py-6 text-base"
                data-testid="pricing-cta-btn">
                {user ? 'Gå till betalning' : 'Skapa konto och börja'}
                <ChevronRight className="ml-2" size={18} />
              </Button>
            </motion.div>

            <p className="text-xs text-[#777777]">Säker betalning via Stripe — kort, Swish och Klarna.</p>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
}
